import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { CellContext, ColumnDef, ExpandedState, Row } from '@tanstack/react-table';
import { DataGrid } from '@/components/DataGrid/DataGrid';
import { useServerDataGrid } from '@/components/DataGrid/useServerDataGrid';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useAlert } from '@/components/ConfirmDialog';
import { downloadExport, ExportSessionExpiredError } from './exportDownload';
import { TakataCallLog } from './TakataCallLog';
import { TakataFilterSidebar } from './TakataFilterSidebar';
import { formatCallDate, formatCallDateTime, formatUniqueContactId, waveLabel } from './takataFormat';
import {
  ALL_WAVES,
  buildExportUrl,
  takataCallKeys,
  useTakataCallsBootstrap,
  useTakataCallsFilterOptions,
  type TakataCallRow,
} from './useTakataCalls';
import '@/styles/stla/stla_shared.scss';
import '@/styles/stla/stla_datatables.scss';
import '@/styles/stla/takata-calls.scss';

/**
 * Replaces `Pages/Stellantis/TakataCallList.cshtml`.
 *
 * The only Stellantis grid paged on the server, so it is the one that exercises
 * the `useServerDataGrid` contract end to end: 16 columns, a filter rail whose
 * keys the controller reads back, an expandable call log fetched per row, and a
 * server-rendered XLSX export.
 *
 * There is no global search box, because the Razor grid did not have one either
 * (`dom: 'Blrtip'` omits `f`). The controller still accepts `search`, and the
 * export still forwards it, so adding one later is a UI change only.
 */

/** Filter keys typed into, and so debounced; the rest apply on change. */
const TEXT_FILTER_KEYS = ['physOut', 'actorCode', 'town', 'address'];

const COLLAPSE_STORAGE_KEY = 'tkFiltersCollapsed';

export function TakataCallsPage() {
  const { t } = useTranslation('mystery/dealerlist');
  const alert = useAlert();
  const [searchParams, setSearchParams] = useSearchParams();

  // The wave lives in the URL, as it did when the selector submitted a GET form,
  // so a link to a particular wave still works.
  const waveParam = searchParams.get('wave');
  const { data: bootstrap } = useTakataCallsBootstrap(waveParam);
  const wave = waveParam ?? bootstrap?.selectedWave ?? null;

  const { data: filterOptions } = useTakataCallsFilterOptions(wave);

  const [textFilters, setTextFilters] = useState<Record<string, string>>({});
  const [choiceFilters, setChoiceFilters] = useState<Record<string, string>>({});
  const debouncedText = useDebouncedValue(textFilters, 350);

  // What the controls show: the text boxes have to echo each keystroke even
  // though the query behind them trails by 350ms.
  const visibleFilters = useMemo(
    () => ({ ...textFilters, ...choiceFilters }),
    [textFilters, choiceFilters],
  );

  // What goes on the wire. Wave rides along in the bag so that switching it is
  // part of the query key and refetches, instead of re-rendering stale rows.
  const filters = useMemo(
    () => ({ ...debouncedText, ...choiceFilters, wave: wave ?? undefined }),
    [debouncedText, choiceFilters, wave],
  );

  const { gridProps, requestBody, resetToFirstPage } = useServerDataGrid<TakataCallRow>({
    endpoint: '/api/stla/takata/calls/query',
    queryKey: takataCallKeys.grid,
    initialPageSize: 10,
    initialSorting: [{ id: 'outcomeDate', desc: true }],
    filters,
    enabled: wave != null,
  });

  // Page 7 of the old result set means nothing in the new one.
  useEffect(() => resetToFirstPage(), [filters, resetToFirstPage]);

  const [expanded, setExpanded] = useState<ExpandedState>({});

  // Rows from a different wave or filter set are not the rows that were open.
  useEffect(() => setExpanded({}), [filters]);

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });

  const setCollapsedPersisted = useCallback((next: boolean) => {
    setCollapsed(next);
    try {
      localStorage.setItem(COLLAPSE_STORAGE_KEY, next ? '1' : '0');
    } catch {
      /* storage disabled */
    }
  }, []);

  const handleFilterChange = useCallback((key: string, value: string) => {
    const setter = TEXT_FILTER_KEYS.includes(key) ? setTextFilters : setChoiceFilters;
    setter((current) => ({ ...current, [key]: value }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setTextFilters({});
    setChoiceFilters({});
  }, []);

  // Which waves carry a usable call log. Unknown until the bootstrap lands, and an
  // empty set then hides every expander rather than offering a log that is not there.
  const callLogWaves = useMemo(
    () => new Set(bootstrap?.callLogWaves ?? []),
    [bootstrap?.callLogWaves],
  );

  // A restricted wave carries none of the call-log data: no history to expand, and
  // no campaign entry / contact id either — those columns arrived with the same
  // import. ?wave=all is not restricted: it mixes both kinds.
  //
  // It is still exported: the file gets its final call per dealer and no history,
  // which the server decides, so the button is offered on every wave.
  const waveRestricted = wave !== ALL_WAVES && !callLogWaves.has(wave ?? '');

  const columns = useMemo<ColumnDef<TakataCallRow, unknown>[]>(
    () => [
      {
        id: 'expander',
        header: () => null,
        enableSorting: false,
        size: 34,
        cell: ({ row }) => {
          // No button at all on the restricted waves; the cell stays so the column
          // keeps its width, and ?wave=all can mix both kinds of row on one page.
          if (!callLogWaves.has(row.original.wave) || row.original.campaignEntryId == null) return null;
          return (
            <button
              type="button"
              className="tk-log-toggle-btn"
              aria-expanded={row.getIsExpanded()}
              aria-label={
                row.getIsExpanded()
                  ? t('TakataCalls.Log.Hide', 'Hide call log')
                  : t('TakataCalls.Log.Show', 'Show call log')
              }
              title={
                row.getIsExpanded()
                  ? t('TakataCalls.Log.Collapse', 'Collapse call log')
                  : t('TakataCalls.Log.Expand', 'Expand call log')
              }
              onClick={row.getToggleExpandedHandler()}
            >
              {row.getIsExpanded() ? '▾' : '▸'}
            </button>
          );
        },
      },
      { id: 'physOut', accessorKey: 'physOut', header: t('TakataCalls.Col.PhysOut', 'PhysOut') },
      { id: 'actorCode', accessorKey: 'actorCode', header: t('TakataCalls.Col.ActorCode', 'Actor Code') },
      { id: 'companyName', accessorKey: 'companyName', header: t('TakataCalls.Col.CompanyName', 'Company Name') },
      { id: 'brand', accessorKey: 'brand', header: t('TakataCalls.Col.Brand', 'Brand') },
      { id: 'town', accessorKey: 'town', header: t('TakataCalls.Col.Town', 'Town') },
      { id: 'address', accessorKey: 'address', header: t('TakataCalls.Col.Address', 'Address') },
      {
        id: 'calledPhoneNumber',
        accessorKey: 'calledPhoneNumber',
        header: t('TakataCalls.Col.CalledPhoneNumber', 'Called Phone Number'),
      },
      { id: 'wave', accessorKey: 'wave', header: t('TakataCalls.Col.Wave', 'Wave') },
      { id: 'quarter', accessorKey: 'quarter', header: t('TakataCalls.Col.Quarter', 'Quarter') },
      {
        id: 'outcomeDate',
        accessorKey: 'outcomeDate',
        header: t('TakataCalls.Col.CallDate', 'Call Date (CET)'),
        cell: ({ row }) => formatCallDateTime(row.original.outcomeDate),
      },
      { id: 'status', accessorKey: 'status', header: t('TakataCalls.Col.Status', 'Status') },
      {
        id: 'appointmentDate',
        accessorKey: 'appointmentDate',
        header: t('TakataCalls.Col.AppointmentDate', 'Appointment date'),
        cell: ({ row }) => formatCallDate(row.original.appointmentDate),
      },
      {
        id: 'delay',
        accessorKey: 'delay',
        header: t('TakataCalls.Col.BookingTimes', 'Booking Times'),
        cell: ({ row }) => row.original.delay ?? '',
      },
      {
        id: 'outcomeDetails',
        accessorKey: 'outcomeDetails',
        header: t('TakataCalls.Col.OutcomeDetails', 'Outcome Details'),
      },
      // Dropped whole on a restricted wave, where every row would read "0-". On
      // ?wave=all it stays for the waves that do have ids, and the rows that do
      // not are left blank rather than showing that same placeholder.
      ...(waveRestricted
        ? []
        : [
            {
              // Display-only merge of two columns; there is no such field in the DB.
              id: 'uniqueContactId',
              accessorKey: 'uniqueContactId',
              header: t('TakataCalls.Col.UniqueContactId', 'Unique Contact Id'),
              cell: ({ row }: CellContext<TakataCallRow, unknown>) =>
                callLogWaves.has(row.original.wave)
                  ? formatUniqueContactId(row.original.campaignEntryId, row.original.uniqueContactId)
                  : '',
            },
          ]),
    ],
    [t, callLogWaves, waveRestricted],
  );

  const renderSubRow = useCallback(
    (row: Row<TakataCallRow>) =>
      row.original.campaignEntryId == null ? null : (
        <TakataCallLog campaignEntryId={row.original.campaignEntryId} wave={wave} />
      ),
    [wave],
  );

  const [exporting, setExporting] = useState(false);

  const handleExport = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const url = buildExportUrl(filters, requestBody.search, requestBody.sortBy, requestBody.sortDir);
      await downloadExport(url, `Takata_Calls_${wave ?? 'wave'}.xlsx`);
    } catch (error) {
      // An unexpected body is almost always the login document after the session
      // expired, same as the Razor page assumed.
      if (error instanceof ExportSessionExpiredError) {
        window.location.href = '/auth-login';
        return;
      }
      // `t` here is the mystery/dealerlist namespace; the title comes from shared.
      void alert({
        variant: 'danger',
        title: t('shared:Message.Error', 'Error'),
        message: t('Grid.ExportError', 'Could not export data. Please try again.'),
      });
    } finally {
      setExporting(false);
    }
  }, [exporting, filters, requestBody, wave, t, alert]);

  const dealers = bootstrap?.dealerIdentities ?? [];

  return (
    <div className="calls-page">
      <header className="tk-hero">
        <div className="tk-hero-inner">
          <div className="tk-hero-lead">
            <span className="tk-eyebrow">
              {t('TakataCalls.Eyebrow', 'Stellantis · Quality Monitoring')}
            </span>
            <h1 className="tk-title">{t('TakataCalls.Title', 'Mystery Calls — Takata')}</h1>
            <p className="tk-sub">
              {t(
                'TakataCalls.Subtitle',
                'Mystery-call audit of dealer response to the Takata airbag recall.',
              )}
            </p>
          </div>

          <div className="tk-wave">
            <label htmlFor="waveFilter" className="tk-wave-label">
              {t('TakataCalls.Wave', 'Campaign wave')}
            </label>
            <div className="tk-select">
              <select
                id="waveFilter"
                value={wave ?? ''}
                onChange={(event) => setSearchParams({ wave: event.target.value })}
              >
                <option value={ALL_WAVES}>{t('TakataCalls.Wave.All', 'All waves')}</option>
                {(bootstrap?.availableWaves ?? []).map((w) => (
                  <option key={w} value={w}>
                    {waveLabel(w)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Total calls, plus one chip per dealer for dealer-scoped accounts. The
            result breakdown is left to the table: results have too many types to
            summarise usefully. */}
        <div className="tk-hero-stats">
          <div className="tk-hstat tk-hstat--total">
            <span className="tk-hstat-k">{t('TakataCalls.TotalCalls', 'Total calls')}</span>
            <span className="tk-hstat-v">
              {(bootstrap?.totalCalls ?? 0).toLocaleString('en-US')}
            </span>
          </div>

          {dealers.length > 0 && (
            <div className="tk-hstat tk-hstat--dealers">
              <span className="tk-hstat-k">
                {dealers.length === 1
                  ? t('TakataCalls.Dealer', 'Dealer')
                  : `${t('TakataCalls.Dealers', 'Dealers')} (${dealers.length})`}
              </span>
              <div className="tk-dealer-cards">
                {dealers.map((dealer) => (
                  <div className="tk-dealer-card" key={dealer.vatCode}>
                    <span className="tk-dealer-name">{dealer.companyName}</span>
                    <span className="tk-dealer-vat">
                      {t('TakataCalls.VatNumber', 'VAT Number')}: {dealer.vatCode}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </header>

      <div className={`tk-wrap tk-layout${collapsed ? ' tk-layout--collapsed' : ''}`}>
        <TakataFilterSidebar
          filters={visibleFilters}
          options={filterOptions}
          collapsed={collapsed}
          onChange={handleFilterChange}
          onClear={handleClearFilters}
          onCollapsedChange={setCollapsedPersisted}
        />

        <div className="tk-main">
          <div className="tk-card">
            <div className="tk-card-head">
              <h2>{t('TakataCalls.Records', 'Call records')}</h2>
              {/* The .dt-buttons wrapper is kept even though DataTables is gone:
                  the page stylesheet scopes its export-button overrides to
                  `.tk-card .dt-buttons .btn-export-excel`, so dropping it would
                  leave only the unscoped base style from stla_datatables. */}
              <div className="dt-buttons">
              <button
                type="button"
                className="btn-export-excel"
                onClick={handleExport}
                disabled={exporting}
              >
                <span className="tk-export-content">
                  {exporting ? (
                    <span className="tk-export-spinner" aria-hidden="true" />
                  ) : (
                    <span className="tk-export-icon">
                      <svg
                        className="tk-export-svg"
                        viewBox="0 0 16 16"
                        width="14"
                        height="14"
                        aria-hidden="true"
                        focusable="false"
                      >
                        <rect
                          x="1"
                          y="1"
                          width="14"
                          height="14"
                          rx="2"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.4"
                        />
                        <line x1="1" y1="6" x2="15" y2="6" stroke="currentColor" strokeWidth="1.2" />
                        <line x1="1" y1="10.5" x2="15" y2="10.5" stroke="currentColor" strokeWidth="1.2" />
                        <line x1="6" y1="1" x2="6" y2="15" stroke="currentColor" strokeWidth="1.2" />
                      </svg>
                    </span>
                  )}
                  <span className="tk-export-label">
                    {exporting
                      ? t('Grid.Exporting', 'Exporting…')
                      : t('Grid.ExportExcel', 'Export Excel')}
                  </span>
                </span>
              </button>
              </div>
            </div>

            <DataGrid<TakataCallRow>
              {...gridProps}
              columns={columns}
              // The call's own key. campaignEntryId/uniqueContactId look like an
              // identity but are 0/"" on the waves imported before they existed, so
              // every such row claimed the id "0-" and React kept the duplicates
              // mounted: switching away from one of those waves left its rows behind.
              getRowId={(row) => String(row.callId)}
              expanded={expanded}
              onExpandedChange={setExpanded}
              renderSubRow={renderSubRow}
              pageSizeOptions={[10, 25, 50, 100]}
              emptyMessage={t('Grid.EmptyTable', 'No calls available')}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
